import BareMobileInput, {
  CountryDropdownOption,
} from "components/DesignSystem/MobileInput/BareMobileInput";
import { Field, FieldProps, getIn } from "formik";
import {
  AsYouType,
  CountryCode,
  parsePhoneNumberFromString,
} from "libphonenumber-js/min";
import React, { ReactNode, useRef } from "react";
import { InputProps } from "../TextInput/TextInput";
import { InputSize } from "../Input/BareInput";

type MobileInputProps = InputProps & {
  label?: ReactNode;
  customSize?: InputSize;
  validateOnChange?: boolean;
  validateOnBlur?: boolean;
};

const DEFAULT_COUNTRY: CountryCode = "US";

const getNationalFormatted = (value: string) => {
  const number = parsePhoneNumberFromString(value ?? "");
  return number?.formatNational() ?? "";
};

const buildFullNumber = (nationalInput: string, dialCode: string) => {
  const digits = nationalInput.replace(/\D/g, "");
  return `${dialCode}${digits}`;
};

export const MobileInput = ({
  validateOnChange: shouldValidateOnChange = true,
  validateOnBlur: shouldValidateOnBlur = true,
  label,
  customSize,
  tooltipText,
  ...restProps
}: MobileInputProps) => {
  const inputRef = useRef<HTMLInputElement>(null);
  return (
    <Field name={restProps.name} {...restProps}>
      {({
        field,
        form: { touched, setFieldValue, setFieldError, errors, values },
      }: FieldProps) => {
        const error = getIn(errors, field.name);
        const isTouched = touched[field.name as string] as boolean;

        const countryFieldName = `${field.name}_country`;
        const country =
          (getIn(values, countryFieldName) as CountryCode) || DEFAULT_COUNTRY;

        const nationalFormatted = getNationalFormatted(field.value);

        const validateNumber = (fullNumber: string) => {
          const formatter = new AsYouType();
          formatter.input(fullNumber);
          const number = formatter.getNumber();
          if (!number?.nationalNumber) return;

          if (formatter.isValid()) {
            setFieldError(field.name, undefined);
          } else {
            setFieldError(field.name, "Invalid number");
          }
        };

        const handleChange = (
          e: React.ChangeEvent<HTMLInputElement>,
          selectedCountry: CountryDropdownOption
        ) => {
          const fullNumber = buildFullNumber(
            e.target.value,
            selectedCountry.triggerText
          );
          setFieldValue(field.name, fullNumber, false);

          if (shouldValidateOnChange) {
            validateNumber(fullNumber);
          } else if (error) {
            setFieldError(field.name, undefined);
          }

          restProps.onChange?.(e);
        };

        const handleCountryChange = (option: CountryDropdownOption) => {
          setFieldValue(field.name, option.triggerText, false);
          setFieldValue(countryFieldName, option.value);
          setFieldError(field.name, undefined);
          if (inputRef.current) inputRef.current.value = "";
        };

        const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
          if (shouldValidateOnBlur) {
            field.onBlur(e);
            validateNumber(field.value);
          } else {
            setFieldError(field.name, undefined);
          }
        };

        let variant: "error" | undefined;
        if (isTouched && error) variant = "error";

        return (
          <BareMobileInput
            ref={inputRef}
            name={field.name}
            label={typeof label === "string" ? label : undefined}
            infoText={tooltipText}
            variant={variant}
            helperText={
              isTouched && error ? error || "Invalid phone number" : undefined
            }
            defaultValue={nationalFormatted}
            onChange={handleChange}
            onBlur={handleBlur}
            onCountryChange={handleCountryChange}
            disabled={restProps.disabled}
            customSize={customSize}
            countryCode={country}
          />
        );
      }}
    </Field>
  );
};
